<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  badgeCount: { type: [Number, String], default: 0 },
  hideTreeLine: { type: Boolean, default: false },
  thinTreeLine: { type: Boolean, default: false },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});

// Tree-line connector per leaf: vertical line (::before) + rounded elbow on the
// last child (::after). Logical props (start / border-s / rounded-es)
const TREE_CONNECTOR =
  "child-item before:content-[''] before:absolute before:start-0 before:w-0.5 before:h-full before:bg-n-slate-4 first:before:rounded-t last:before:h-1/5 last:after:content-[''] last:after:absolute last:after:start-0 last:after:bottom-[calc(50%_-_2px)] last:after:h-3 last:after:w-2.5 last:after:border-b-2 last:after:border-s-2 last:after:rounded-es last:after:border-n-slate-4";
</script>

<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ps-2 ms-3 relative text-n-slate-11 min-w-0"
    :class="{
      [TREE_CONNECTOR]: !hideTreeLine,
      'before:!w-px last:after:!border-b last:after:!border-s':
        !hideTreeLine && thinTreeLine,
    }"
  >
    <!-- custom (redesign Modeloja · F1): child row keeps the nested h-8 scale + tree
         connector, but adopts the brand active fill (#EFEFEF ≈ bg-n-slate-3), a subtle
         hover, semibold-ink active label and the shell focus ring. -->
    <!-- ⚠️ 34 → 32px em 2026-08-11, junto com o rótulo (16 → 13px). Mesma regra do nível 1:
         a caixa desce com a letra, senão a razão estoura (32/13 = 2,46, na faixa da casa). -->
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="flex h-[32px] items-center gap-2 px-2 py-1 rounded-lg mj-nav-hover group min-w-0 transition-[background,transform] duration-150 ease-[cubic-bezier(.2,.7,.3,1)] active:scale-[.98] focus-visible:outline-2 focus-visible:outline-n-slate-12 focus-visible:outline-offset-2"
      :class="{
        'text-n-slate-12 mj-nav-ativo font-semibold active': active,
      }"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        v-bind="{ label, icon, active, badgeCount }"
      />
      <template v-else>
        <!-- custom (2026-08-07): mesma regra do nível 1 — ícone um degrau abaixo do rótulo. -->
        <!-- ⚠️ A CAIXA vai a 20px (`size-5`) para bater com a `leading-5` do rótulo ao lado
             — a mesma regra do nível 1, pelo mesmo motivo (ver SidebarGroupHeader). O
             DESENHO dentro dela sobe para 17px, seguindo o degrau: o sub-item é sempre um
             passo abaixo do pai, e quem marca o degrau é o desenho, não a caixa. -->
        <span v-if="icon" class="size-5 grid place-content-center rounded-full">
          <Icon
            :icon="icon"
            class="size-4 inline-block shrink-0"
            :class="active ? 'text-n-slate-12' : 'text-n-slate-10'"
          />
        </span>
        <!-- custom (2026-08-07): 14 → 13px, o mesmo corpo do item de nível 1. Quem separa
             os dois níveis é o recuo e a tinta, não o tamanho da letra — dois tamanhos de
             fonte a um ponto de distância não leem como hierarquia, leem como descuido.
             custom (Lote 2): o comentário dizia 13 e o código dizia 13,5 — a intenção e o
             valor tinham divergido, e meio pixel a menos não é decisão de ninguém, é
             deriva. Agora são os 13px da escala, que é o que este texto sempre quis ser. -->
        <!-- custom (2026-08-09): 13 → 14px, acompanhando o item de topo (15px). O
             sub-item fica sempre UM degrau abaixo do pai — é esse degrau que diz, sem
             indentação extra, que ele pertence ao item de cima. -->
        <!-- custom (2026-08-09, tarde): 15 → 16px, sempre UM degrau abaixo do item de topo
             (17px). O degrau é o que diz "isto pertence ao de cima" sem gastar indentação;
             se os dois subissem para o mesmo corpo, a hierarquia sumiria e a lista viraria
             uma fileira plana de onze linhas.
             `leading-5` pelo mesmo motivo do nível 1: é a caixa que casa com a do ícone. -->
        <!-- ⚠️ 16 → 13px em 2026-08-11, um degrau abaixo do nível 1 (14px) como sempre.
             O degrau é o que diz "isto pertence ao de cima" sem gastar indentação — se os
             dois voltassem ao mesmo corpo, a hierarquia sumiria. O desenho desce junto
             (17 → 16px na mesma caixa de 20). Motivo da descida geral: ver a nota do rótulo
             em SidebarGroupHeader — a escada de 2026-08-09 subiu demais e o menu passou a
             competir com o conteúdo da tela.
             `leading-5` fica: é a caixa que casa com a do ícone. -->
        <div class="flex-1 truncate min-w-0 text-[13px] leading-5">
          {{ label }}
        </div>
        <SidebarUnreadBadge :count="badgeCount" />
      </template>
    </component>
  </Policy>
</template>
